import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, TextField,
  Alert, Stepper, Step, StepLabel, Divider, Select, MenuItem,
  FormControl, InputLabel, CircularProgress, Chip
} from '@mui/material';
import FileUpload from '../components/FileUpload';
import api from '../services/api';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const TRADES = ["Soil Engineer","Site Services","Demo","Survey","Civil","Foundation","Framing","Plumbing","HVAC","PG&E / Utilities","Roof","Stucco","Structural","Architectural","Electrical","Insulation","Drywall","Flooring","Painting","Landscaping","Concrete","Windows & Doors","Cabinets & Millwork","Tile & Stone","Masonry","Supplier","Other"];
const STEPS = ['Company Info', 'License & Insurance', 'Payment Details', 'Review & Submit'];

export default function VendorOnboarding() {
  const token = new URLSearchParams(window.location.search).get('token');
  const [vendor, setVendor] = useState(null);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [insuranceFiles, setInsuranceFiles] = useState([]);
  const [w9Files, setW9Files] = useState([]);
  const [form, setForm] = useState({
    company_name: '', contact_name: '', phone: '', address: '', city: '',
    state: '', zip: '', trade_specialty: '', service_area: '', license_number: '',
    license_expiry: '', bank_name: '', account_type: 'checking', routing_number: '',
    account_number_last4: '', payment_method: 'check',
    insurance_cert_url: '', w9_url: '',
  });

  useEffect(() => {
    if (!token) { setError('Invalid onboarding link'); setLoading(false); return; }
    api.get(`/vendors/onboarding/${token}`)
      .then(res => {
        setVendor(res.data);
        setForm(f => ({ ...f, company_name: res.data.company_name || '', contact_name: res.data.contact_name || '',
          phone: res.data.phone || '', trade_specialty: res.data.trade_specialty || '' }));
        setLoading(false);
      })
      .catch(() => { setError('Invalid or expired onboarding link'); setLoading(false); });
  }, [token]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        insurance_cert_url: insuranceFiles[0]?.url || form.insurance_cert_url,
        w9_url: w9Files[0]?.url || form.w9_url,
      };
      await api.put(`/vendors/onboarding/${token}`, payload);
      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit. Please try again.');
    }
    setSubmitting(false);
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f6fa' }}>
      <Card sx={{ borderRadius: 3, p: 4, maxWidth: 400, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ color: '#e53935', mb: 2 }}>Invalid Link</Typography>
        <Typography sx={{ color: '#666' }}>{error}</Typography>
      </Card>
    </Box>
  );

  if (submitted) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f6fa' }}>
      <Card sx={{ borderRadius: 3, p: 6, maxWidth: 500, textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 80, color: '#2e7d32', mb: 2 }} />
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e', mb: 1 }}>You're All Set!</Typography>
        <Typography sx={{ color: '#666', mb: 2 }}>Your onboarding is complete for <strong>{vendor?.project_name}</strong>.</Typography>
        <Typography variant="body2" sx={{ color: '#999' }}>The project owner will review your information and approve your account. You'll receive an email confirmation shortly.</Typography>
      </Card>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f6fa' }}>
      {/* Header */}
      <Box sx={{ background: 'linear-gradient(135deg, #1a237e, #283593)', py: 3, px: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <BusinessIcon sx={{ color: 'white', fontSize: 32 }} />
        <Box>
          <Typography variant="h5" sx={{ color: 'white', fontWeight: 800 }}>Yaplar</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>Vendor Onboarding — {vendor?.project_name}</Typography>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 700, mx: 'auto', py: 4, px: 2 }}>
        <Card elevation={0} sx={{ borderRadius: 3, mb: 3, p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a237e', mb: 0.5 }}>Welcome, {vendor?.contact_name || vendor?.company_name}!</Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>Please complete your vendor profile to get started on <strong>{vendor?.project_name}</strong>.</Typography>
        </Card>

        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>

        <Card elevation={0} sx={{ borderRadius: 3, p: 3 }}>
          {/* Step 0: Company Info */}
          {step === 0 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e', mb: 2 }}>Company Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Company Name *" value={form.company_name}
                    onChange={e => setForm({...form, company_name: e.target.value})}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Contact Name *" value={form.contact_name}
                    onChange={e => setForm({...form, contact_name: e.target.value})}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Phone *" value={form.phone}
                    onChange={e => setForm({...form, phone: e.target.value})}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Trade Specialty</InputLabel>
                    <Select value={form.trade_specialty} onChange={e => setForm({...form, trade_specialty: e.target.value})}
                      label="Trade Specialty" sx={{ borderRadius: 2 }}>
                      {TRADES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Street Address" value={form.address}
                    onChange={e => setForm({...form, address: e.target.value})}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </Grid>
                <Grid item xs={5}>
                  <TextField fullWidth label="City" value={form.city}
                    onChange={e => setForm({...form, city: e.target.value})}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </Grid>
                <Grid item xs={3}>
                  <TextField fullWidth label="State" value={form.state}
                    onChange={e => setForm({...form, state: e.target.value})}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </Grid>
                <Grid item xs={4}>
                  <TextField fullWidth label="ZIP" value={form.zip}
                    onChange={e => setForm({...form, zip: e.target.value})}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Service Area" value={form.service_area} multiline rows={2}
                    onChange={e => setForm({...form, service_area: e.target.value})}
                    placeholder="e.g. Dallas-Fort Worth metro area"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Step 1: License & Insurance */}
          {step === 1 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e', mb: 2 }}>License & Insurance</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="License Number" value={form.license_number}
                    onChange={e => setForm({...form, license_number: e.target.value})}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="License Expiry Date" type="date" value={form.license_expiry}
                    onChange={e => setForm({...form, license_expiry: e.target.value})}
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </Grid>
              </Grid>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e', mb: 1 }}>Insurance Certificate</Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>Upload your certificate of insurance (PDF or image)</Typography>
              <FileUpload onFilesChange={setInsuranceFiles} />
              <Divider sx={{ my: 3 }} />
              {/* W9 hidden for now */}
            </Box>
          )}

          {/* Step 2: Payment */}
          {step === 2 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e', mb: 2 }}>Payment Details</Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Preferred Payment Method</InputLabel>
                <Select value={form.payment_method} onChange={e => setForm({...form, payment_method: e.target.value})}
                  label="Preferred Payment Method" sx={{ borderRadius: 2 }}>
                  <MenuItem value="check">🖊️ Check</MenuItem>
                  <MenuItem value="ach">🏦 ACH / Bank Transfer</MenuItem>
                  <MenuItem value="zelle">⚡ Zelle</MenuItem>
                  <MenuItem value="wire">🔗 Wire Transfer</MenuItem>
                </Select>
              </FormControl>
              {(form.payment_method === 'ach' || form.payment_method === 'wire') && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Bank Name" value={form.bank_name}
                      onChange={e => setForm({...form, bank_name: e.target.value})}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>Account Type</InputLabel>
                      <Select value={form.account_type} onChange={e => setForm({...form, account_type: e.target.value})}
                        label="Account Type" sx={{ borderRadius: 2 }}>
                        <MenuItem value="checking">Checking</MenuItem>
                        <MenuItem value="savings">Savings</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="Routing Number" value={form.routing_number}
                      onChange={e => setForm({...form, routing_number: e.target.value})}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Last 4 digits of Account Number" value={form.account_number_last4}
                      onChange={e => setForm({...form, account_number_last4: e.target.value.slice(0,4)})}
                      inputProps={{ maxLength: 4 }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                </Grid>
              )}
            </Box>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e', mb: 2 }}>Review & Submit</Typography>
              {[
                { label: 'Company', value: form.company_name },
                { label: 'Contact', value: form.contact_name },
                { label: 'Phone', value: form.phone },
                { label: 'Trade', value: form.trade_specialty },
                { label: 'Address', value: [form.address, form.city, form.state].filter(Boolean).join(', ') },
                { label: 'License #', value: form.license_number },
                { label: 'Payment Method', value: form.payment_method },
              ].map((item, i) => item.value && (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #f5f6fa' }}>
                  <Typography variant="body2" sx={{ color: '#999' }}>{item.label}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.value}</Typography>
                </Box>
              ))}
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {insuranceFiles.length > 0 && <Chip label="✓ Insurance Certificate" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />}
                {/* W9 chip hidden */}
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button disabled={step === 0} onClick={() => setStep(s => s - 1)} sx={{ borderRadius: 2 }}>Back</Button>
            {step < 3 ? (
              <Button variant="contained" onClick={() => setStep(s => s + 1)}
                disabled={step === 0 && (!form.company_name || !form.contact_name || !form.phone)}
                sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>
                Next
              </Button>
            ) : (
              <Button variant="contained" onClick={handleSubmit} disabled={submitting}
                startIcon={<CheckCircleIcon />}
                sx={{ bgcolor: '#2e7d32', borderRadius: 2, px: 3 }}>
                {submitting ? 'Submitting...' : 'Submit Onboarding'}
              </Button>
            )}
          </Box>
        </Card>
      </Box>
    </Box>
  );
}
